import type { JSX } from 'react';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { initials } from '../lib/format.js';
import { IconCheck, IconInfo, IconWarning } from './icons.js';

/** Базовые кирпичики интерфейса. Всё остальное собирается из них. */

/* ------------------------------------------------------------------ */
/* Кнопка                                                              */
/* ------------------------------------------------------------------ */

type ButtonVariant = 'default' | 'primary' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  /** Показывает крутилку и блокирует повторные нажатия. */
  loading?: boolean;
}

export function Button({
  variant = 'default',
  size = 'md',
  block,
  loading,
  disabled,
  children,
  className = '',
  ...rest
}: ButtonProps): JSX.Element {
  const classes = [
    'btn',
    variant !== 'default' ? `btn--${variant}` : '',
    size !== 'md' ? `btn--${size}` : '',
    block ? 'btn--block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button className={classes} disabled={disabled || loading} {...rest}>
      {loading && <span className="spinner" aria-hidden="true" />}
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Поля ввода                                                          */
/* ------------------------------------------------------------------ */

interface FieldWrapperProps {
  label?: string;
  hint?: string;
  error?: string | null;
  children: (id: string) => ReactNode;
}

function FieldWrapper({ label, hint, error, children }: FieldWrapperProps): JSX.Element {
  const id = useId();
  return (
    <div className="field">
      {label && (
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
      )}
      {children(id)}
      {error ? <span className="field__error">{error}</span> : hint ? <span className="field__hint">{hint}</span> : null}
    </div>
  );
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label?: string;
  hint?: string;
  error?: string | null;
}

export function TextField({ label, hint, error, className = '', ...rest }: TextFieldProps): JSX.Element {
  return (
    <FieldWrapper label={label} hint={hint} error={error}>
      {(id) => (
        <input id={id} className={`input ${error ? 'input--invalid' : ''} ${className}`.trim()} {...rest} />
      )}
    </FieldWrapper>
  );
}

interface TextAreaFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label?: string;
  hint?: string;
  error?: string | null;
}

export function TextAreaField({ label, hint, error, className = '', ...rest }: TextAreaFieldProps): JSX.Element {
  return (
    <FieldWrapper label={label} hint={hint} error={error}>
      {(id) => (
        <textarea id={id} className={`textarea ${error ? 'input--invalid' : ''} ${className}`.trim()} {...rest} />
      )}
    </FieldWrapper>
  );
}

interface SelectFieldProps {
  label?: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}

export function SelectField({ label, hint, value, onChange, options }: SelectFieldProps): JSX.Element {
  return (
    <FieldWrapper label={label} hint={hint}>
      {(id) => (
        <select id={id} className="select input" value={value} onChange={(event) => onChange(event.target.value)}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldWrapper>
  );
}

/* ------------------------------------------------------------------ */
/* Загрузка картинки                                                   */
/* ------------------------------------------------------------------ */

interface ImageUploadButtonProps {
  onUploaded: (url: string) => void;
  onError?: (message: string) => void;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: ButtonVariant;
}

/**
 * Кнопка «загрузить фото».
 *
 * Настоящий `<input type="file">` уродлив и по-разному выглядит в браузерах,
 * поэтому прячем его и кликаем по нему из красивой кнопки. На телефоне такой
 * ввод сам предлагает выбор «камера или галерея» — это и есть запасной путь,
 * когда сайт не дал нам фотографию.
 */
export function ImageUploadButton({
  onUploaded,
  onError,
  label = 'Загрузить фото',
  size = 'md',
  variant = 'ghost',
}: ImageUploadButtonProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function handleChange(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    // Сбрасываем значение сразу: иначе повторный выбор того же файла не сработает.
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      onError?.('Это не картинка');
      return;
    }

    setBusy(true);
    try {
      const { api } = await import('../lib/api.js');
      const { url } = await api.uploads.image(file);
      onUploaded(url);
    } catch (error) {
      const { ApiError } = await import('../lib/api.js');
      onError?.(error instanceof ApiError ? error.message : 'Не удалось загрузить картинку.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="visually-hidden"
        onChange={handleChange}
        tabIndex={-1}
      />
      <Button type="button" variant={variant} size={size} loading={busy} onClick={() => inputRef.current?.click()}>
        {busy ? 'Загружаем…' : label}
      </Button>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Аватар                                                              */
/* ------------------------------------------------------------------ */

interface AvatarProps {
  name: string;
  src?: string | null;
  size?: 'sm' | 'md' | 'lg';
}

export function Avatar({ name, src, size = 'md' }: AvatarProps): JSX.Element {
  const className = `avatar ${size !== 'md' ? `avatar--${size}` : ''}`.trim();
  return (
    <div className={className} title={name} aria-hidden="true">
      {src ? <img src={src} alt="" loading="lazy" /> : initials(name)}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Метки и подсказки                                                   */
/* ------------------------------------------------------------------ */

interface BadgeProps {
  children: ReactNode;
  tone?: 'default' | 'accent' | 'success' | 'warning' | 'danger' | 'solid' | 'gold';
}

export function Badge({ children, tone = 'default' }: BadgeProps): JSX.Element {
  return <span className={`badge ${tone !== 'default' ? `badge--${tone}` : ''}`.trim()}>{children}</span>;
}

interface NoticeProps {
  children: ReactNode;
  tone?: 'info' | 'warning' | 'danger' | 'success';
  /**
   * Свой значок вместо положенного тону. Нужен там, где полоса говорит не о
   * состоянии, а о сути: тайна брони, ожидание, установка на телефон.
   */
  icon?: ReactNode;
}

/**
 * Значок полосы по её тону.
 *
 * Раньше значок передавали руками на каждом из двадцати восьми мест, и он
 * разъезжался: у одной беды стояло ⚠️, у другой ничего, удача была то ✓, то
 * без значка вовсе. Тон полосы и так сказан в разметке — из него значок и
 * следует. Передать свой по-прежнему можно, но уже осознанно.
 */
const NOTICE_ICON: Record<NonNullable<NoticeProps['tone']>, JSX.Element> = {
  info: <IconInfo size={19} />,
  warning: <IconWarning size={19} />,
  danger: <IconWarning size={19} />,
  success: <IconCheck size={19} />,
};

export function Notice({ children, tone = 'info', icon }: NoticeProps): JSX.Element {
  return (
    <div className={`notice notice--${tone}`} role={tone === 'danger' ? 'alert' : undefined}>
      <span className="notice__icon" aria-hidden="true">
        {icon ?? NOTICE_ICON[tone]}
      </span>
      <div className="grow">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Состояния                                                           */
/* ------------------------------------------------------------------ */

interface EmptyStateProps {
  /**
   * Значок пустого состояния. Раньше сюда передавали только эмодзи, теперь
   * можно и рисованный значок — тип расширен, а не заменён, поэтому строка
   * по-прежнему годится и старые вызовы не ломаются.
   */
  icon: ReactNode;
  title: string;
  text?: string;
  action?: ReactNode;
}

export function EmptyState({ icon, title, text, action }: EmptyStateProps): JSX.Element {
  return (
    <div className="empty">
      <div className="empty__icon" aria-hidden="true">
        {icon}
      </div>
      <div className="empty__title">{title}</div>
      {text && <p className="empty__text">{text}</p>}
      {action}
    </div>
  );
}

export function Loading({ label = 'Загружаем…' }: { label?: string }): JSX.Element {
  return (
    <div className="empty" role="status">
      <span className="spinner" />
      <span className="muted small">{label}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Модальное окно                                                      */
/* ------------------------------------------------------------------ */

interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

export function Modal({ open, title, onClose, children, footer }: ModalProps): JSX.Element | null {
  const backdropRef = useRef<HTMLDivElement>(null);

  // Escape закрывает окно, а фон под ним не должен прокручиваться — иначе на
  // телефоне страница «уезжает» под открытой формой.
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);

    /*
     * Фон держим на месте прибиванием, а не запретом прокрутки.
     *
     * `overflow: hidden` на body Safari на iPhone попросту не слушается —
     * ни в браузере, ни в приложении с домашнего экрана. Открыв форму
     * желания, человек листал её, дочитывал до конца, а дальше палец
     * начинал листать список ПОД формой: фон уезжал, и вернуть его,
     * не закрыв форму, было невозможно.
     *
     * Прибиваем body к экрану, запомнив, на каком месте человек читал,
     * и возвращаем это место при закрытии. Иначе список после каждой
     * формы прыгал бы в начало — а он длинный.
     */
    const прежние = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
    };
    const местоЧтения = window.scrollY;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${местоЧтения}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = прежние.overflow;
      document.body.style.position = прежние.position;
      document.body.style.top = прежние.top;
      document.body.style.left = прежние.left;
      document.body.style.right = прежние.right;
      document.body.style.width = прежние.width;
      window.scrollTo(0, местоЧтения);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      ref={backdropRef}
      onMouseDown={(event) => {
        // Закрываем только по клику именно по фону, а не по окну.
        if (event.target === backdropRef.current) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="modal">
        <div className="modal__header">
          <h2 style={{ fontSize: '1.1rem' }}>{title}</h2>
          <Button variant="ghost" size="sm" className="btn--icon" onClick={onClose} aria-label="Закрыть">
            ✕
          </Button>
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__footer">{footer}</div>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Подтверждение опасного действия                                     */
/* ------------------------------------------------------------------ */

interface ConfirmProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

export function Confirm({
  open,
  title,
  message,
  confirmLabel = 'Удалить',
  onConfirm,
  onCancel,
  loading,
}: ConfirmProps): JSX.Element | null {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} block>
            Отмена
          </Button>
          <Button variant="danger" onClick={onConfirm} loading={loading} block>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="muted">{message}</p>
    </Modal>
  );
}
